import { useCallback, useEffect, useMemo, useState } from "react";

import { useQueryParams } from "@hooks";

import {
  getBuildingByCheckInOrWorkspaceReservation,
  getUser,
  getAllBuildings,
  useAppSelector,
  getDefaultBuilding,
} from "@lib/store";

import { startOfDay } from "date-fns";

import { QueryParamConstants } from "@lib/logic";

export const HaN_Previous_Tab = "HaN_Previous_Tab";

const queryParamKeys = QueryParamConstants.hereAndNow.explore;

export const useHereAndNowBuildingSelection = () => {
  const { buildingId: linkedBuildingId } = useQueryParams([
    queryParamKeys.search,
    queryParamKeys.buildingId,
    queryParamKeys.resourceId,
    QueryParamConstants.intent,
  ]);

  const { allBuildings, loggedInUserId, defaultBuilding } = useAppSelector(state => ({
    allBuildings: getAllBuildings(state),
    loggedInUserId: getUser(state).id,
    defaultBuilding: getDefaultBuilding(state),
  }));

  const todayISOString = useMemo(() => startOfDay(new Date()).toISOString(), []);

  const reservationBuildingId = useAppSelector(
    state => getBuildingByCheckInOrWorkspaceReservation(state, todayISOString)?.id,
  );

  const buildingIdsSet = useMemo(() => new Set(allBuildings.map(building => building.id)), [allBuildings]);

  const sessionBuildingIdKey = useMemo(() => `${loggedInUserId}_buildingId`, [loggedInUserId]);

  const getInitialBuildingId = useCallback(() => {
    if (linkedBuildingId) return linkedBuildingId;

    const sessionBuildingId = sessionStorage.getItem(sessionBuildingIdKey);
    if (sessionBuildingId && buildingIdsSet.has(sessionBuildingId)) {
      return sessionBuildingId;
    }

    return reservationBuildingId || defaultBuilding?.id || allBuildings[0]?.id || "";
  }, [
    linkedBuildingId,
    sessionBuildingIdKey,
    buildingIdsSet,
    reservationBuildingId,
    defaultBuilding?.id,
    allBuildings,
  ]);

  const [buildingId, setBuildingId] = useState(getInitialBuildingId);

  // Clean up invalid session storage
  useEffect(() => {
    const sessionBuildingId = sessionStorage.getItem(sessionBuildingIdKey);
    if (allBuildings.length && sessionBuildingId && !buildingIdsSet.has(sessionBuildingId)) {
      sessionStorage.removeItem(sessionBuildingIdKey);
    }
  }, [buildingIdsSet, sessionBuildingIdKey, allBuildings.length]);

  // Update session storage and handle reservation changes
  useEffect(() => {
    if (buildingId) {
      sessionStorage.setItem(sessionBuildingIdKey, buildingId);
    } else if (reservationBuildingId) {
      setBuildingId(reservationBuildingId);
    }
  }, [buildingId, reservationBuildingId, sessionBuildingIdKey]);

  return [buildingId, setBuildingId] as const;
};
